import {
  Button,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import {
  formatServerMoney,
  isSufficientBalance,
  parseFloatValue,
  parseIncompleteFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';
import { ChangeEvent, MouseEventHandler, useCallback, useMemo } from 'react';
import { ChevronRightIcon } from '../../icons';
import { useAnchor } from '../../hooks/useAnchor';
import { useTranslation } from 'react-i18next';
import { AvatarCurrency } from '../Avatar/AvatarCurrency';
import { UiAccount } from '../../types';
import { AccountListItemContent } from '../List/AccountListItem';

export type AccountAmountValue<Account extends UiAccount = UiAccount> = {
  account: Account;
  clientAmount: string;
};

export interface AccountAmountFieldProps<Account extends UiAccount> {
  value: AccountAmountValue<Account> | undefined;
  options: Account[];
  autoFocus?: boolean;
  helperSellAll?: boolean;
  validateBalance?: boolean;
  sellAllWord?: string;
  onChange: (value: AccountAmountValue<Account>) => void;
  renderItemContent?: (account: Account) => React.ReactNode;
}

function CurrencyAdornment({
  size,
  currency,
  onClick,
}: {
  size: number;
  currency: string | null | undefined;
  onClick: MouseEventHandler;
}) {
  return (
    <ButtonBase onClick={onClick}>
      {currency ? <AvatarCurrency currency={currency} size={size} /> : null}
      <Typography
        component="span"
        sx={{
          fontSize: size,
          display: 'inline-block',
          paddingInlineStart: 1,
        }}
      >
        {currency}
      </Typography>
      <ChevronRightIcon />
    </ButtonBase>
  );
}

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    height: '65px',
    paddingRight: 0,
  },
  '& .MuiInputBase-input': {
    fontSize: 20,
    padding: '18px 16px',
    height: '100%',
    boxSizing: 'border-box',
  },
  '& .MuiInputAdornment-root': {
    fontSize: 20,
    maxHeight: 'unset',
    height: '100%',
    marginLeft: 0,
    paddingRight: '8px',

    '& button': {
      height: '100%',
      width: '100%',
    },
  },
});

export function AccountAmountField<Account extends UiAccount>({
  value,
  options,
  autoFocus,
  helperSellAll,
  validateBalance,
  sellAllWord,
  onChange,
  renderItemContent,
}: AccountAmountFieldProps<Account>) {
  const { t } = useTranslation();
  const anchor = useAnchor<HTMLButtonElement>();
  const currency = value?.account.balance.currency;

  const handleAccountChange = useCallback(
    (accountId: string) => {
      const account = options.find(({ id }) => id === accountId);
      if (account) {
        onChange({
          account,
          clientAmount: value?.clientAmount ?? '',
        });
      }
      anchor.onClose();
    },
    [anchor, onChange, options, value?.clientAmount],
  );

  const handleAmountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (value) {
        const clientAmount = parseIncompleteFloatValue(event.target.value);
        onChange({
          clientAmount,
          account: value.account,
        });
      }
    },
    [value, onChange],
  );

  const handleSellAll = useCallback(() => {
    if (value) {
      onChange({
        account: value.account,
        clientAmount: String(serverAmountToClientAmount(value.account.balance)),
      });
    }
  }, [onChange, value]);

  const isBalanceValid = useMemo(() => {
    if (validateBalance && value) {
      const parsed = parseFloatValue(value.clientAmount);
      return isSufficientBalance(parsed, value.account.balance);
    }

    return true;
  }, [value, validateBalance]);

  const helperText = useMemo(() => {
    if (value) {
      if (!isBalanceValid) {
        return t('You have only {{money}} available', {
          money: formatServerMoney(value.account.balance),
        });
      }
      if (helperSellAll) {
        return (
          <Button variant="text" color="inherit" onClick={handleSellAll}>
            {t('{{sellAllWord}} all {{money}}', {
              money: formatServerMoney(value.account.balance),
              sellAllWord: sellAllWord ?? t('Sell'),
            })}
          </Button>
        );
      }
    }
  }, [value, isBalanceValid, handleSellAll, helperSellAll, sellAllWord, t]);

  return (
    <>
      <StyledTextField
        fullWidth
        value={value?.clientAmount ?? ''}
        onChange={handleAmountChange}
        placeholder="0"
        helperText={helperText ?? ' '}
        error={!isBalanceValid}
        autoFocus={autoFocus}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <CurrencyAdornment size={20} currency={currency} onClick={anchor.onOpen} />
                ) : (
                  <CircularProgress size={20} />
                )}
              </InputAdornment>
            ),
          },
          htmlInput: { inputMode: 'decimal' },
        }}
      />
      <Menu anchorEl={anchor.element} open={anchor.open} onClose={anchor.onClose}>
        {options.map((account) => (
          <MenuItem
            key={account.id}
            selected={account.id === value?.account.id}
            onClick={() => handleAccountChange(account.id)}
          >
            {renderItemContent ? (
              renderItemContent(account)
            ) : (
              <AccountListItemContent account={account} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
